import { z } from "zod";
export const taskIdSchema = z.string().trim().min(1);
export const runIdSchema = z.string().trim().min(1);
export const taskRunSourceSchema = z.enum(["web", "cli", "slack", "teams", "copilot", "api", "mcp"]);
export const taskRunCapabilitySchema = z.enum([
    "readonly-data",
    "writeback",
    "web-research",
    "browser",
    "terminal",
    // Desk's tools mid-turn, for a run its bound Desk asked for (docs/specs/twin-desk-client-tools.md): never a
    // tenant_policy grant.
    "desk-client",
]);
export const taskRunInteractionModeSchema = z.enum(["foreground", "background"]);
// Read compatibility only: historical Postgres/Convex-imported rows may still
// contain "worker". New run creation uses activeTaskRunRuntimeBackendSchema.
export const taskRunRuntimeBackendSchema = z.enum(["worker", "electric"]);
export const taskRunExecutionTierValues = [
    "governed",
    "exploratory",
    "mixed",
    "routing_failed",
    "none",
];
export const taskRunExecutionTierSchema = z.enum(taskRunExecutionTierValues);
export const sessionRoutingArchitectureValues = [
    "legacy_router",
    "postgres_plugin_router",
    "task_agent_owned_retrieval",
    "direct_governed",
    "direct_sql",
    "direct_sql_enhanced",
    "direct_sql_staging",
    "v1",
    "v1-sql",
    "v2-hybrid",
    "v3",
];
export const sessionRoutingArchitectureSchema = z.enum(sessionRoutingArchitectureValues);
export const sessionRoutingArchitecturePolicies = {
    legacy_router: {
        usesSessionContracts: true,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    postgres_plugin_router: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: true,
        allowedAsTargetDefault: true,
    },
    task_agent_owned_retrieval: {
        usesSessionContracts: true,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    direct_governed: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    direct_sql: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: false,
    },
    direct_sql_enhanced: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    direct_sql_staging: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    v1: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: true,
        allowedAsTargetDefault: false,
    },
    "v1-sql": {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: false,
        allowedAsTargetDefault: true,
    },
    "v2-hybrid": {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: true,
        allowedAsTargetDefault: true,
    },
    v3: {
        usesSessionContracts: false,
        usesPostgresPluginCatalog: true,
        allowedAsTargetDefault: true,
    },
};
export function canonicalSessionRoutingArchitecture(value) {
    if (value === "direct_sql")
        return "v1-sql";
    if (value === "v1")
        return "v2-hybrid";
    return value;
}
export function sessionRoutingArchitecturePolicy(value) {
    const architecture = sessionRoutingArchitectureSchema.parse(value);
    return sessionRoutingArchitecturePolicies[architecture];
}
export function sessionRoutingArchitectureUsesPostgresCatalog(value) {
    if (value === null || value === undefined)
        return false;
    return sessionRoutingArchitecturePolicy(value).usesPostgresPluginCatalog;
}
/**
 * The hybrid family: v2-hybrid (and its historical alias v1) and its successor v3 share the
 * capability catalogue, tool discovery and governed execution; they differ in routing policy.
 */
export function isHybridRoutingArchitecture(value) {
    return value === "v1" || value === "v2-hybrid" || value === "v3";
}
export const governedPromptProfileSchema = z.enum([
    "minimal",
    "modest",
    "fat",
]);
export const taskAgentStepReasoningEnabledSchema = z.boolean();
export const taskAgentCacheOptimizationEnabledSchema = z.boolean();
export const openAICodexOriginatorSchema = z.enum([
    "pi",
    "codex_cli_rs",
    "codex_sdk_ts",
]);
export const openAICodexAlignmentSchema = z.enum([
    "default",
    "codex_max",
]);
export const openAICodexExperimentSchema = z.object({
    fastMode: z.boolean().default(false),
    originator: openAICodexOriginatorSchema.default("pi"),
    alignment: openAICodexAlignmentSchema.default("default"),
}).strict();
export const taskRunRoutingFailureCodeSchema = z.enum([
    "no_governed_contract",
    "router_unavailable",
    "source_unavailable",
]);
export const taskRunRoutingFailureSchema = z.object({
    code: taskRunRoutingFailureCodeSchema,
    clarificationRequired: z.literal(true),
    sourceSlug: z.string().trim().min(1).max(120).optional(),
});
export const activeTaskRunRuntimeBackendSchema = z.literal("electric");
export const taskRunStatusSchema = z.enum([
    "queued",
    "running",
    "completed",
    "failed",
    "cancelled",
]);
// Closed failure taxonomy for runs (audit M1.5): set at the failure boundary,
// persisted on task_runs.error_code, and exposed to UI/operators/retries.
export const runErrorCodeSchema = z.enum([
    "provider_auth",
    "provider_rate_limit",
    "provider_error",
    "context_window_exceeded",
    "runtime_unavailable",
    "tool_failed",
    "timeout",
    "cancelled",
    "session_contract_incomplete",
    "memory_candidate_overflow",
    // A workflow's dry run that ran and failed its own test: feedback for whoever builds the workflow, not a fault.
    "workflow_sample_failed",
    "internal",
]);
export const taskRunEventPayloadSchema = z
    .record(z.string(), z.unknown())
    .default({});
/**
 * An ISO 8601 date-time with a `Z` or numeric offset, seconds optional.
 *
 * zod 4.5 made `z.iso.datetime()` require seconds, as RFC 3339 does. Agent
 * tools document their time bounds as ISO 8601, which also allows minute
 * precision (`2026-09-01T09:30Z`), and zod 4.3 accepted it; this union is
 * zod's documented way to keep accepting both.
 */
export const offsetDateTimeWithOptionalSecondsSchema = z.union([z.iso.datetime({ offset: true }), z.iso.datetime({ offset: true, precision: -1 })], { error: "Invalid ISO datetime" });
/**
 * A UTC (`Z`) ISO 8601 date-time, seconds optional: what `z.iso.datetime()`
 * accepted before zod 4.5, for hand-written and stored values that may carry
 * minute precision (`2026-09-01T09:30Z`).
 */
export const utcDateTimeWithOptionalSecondsSchema = z.union([z.iso.datetime(), z.iso.datetime({ precision: -1 })], { error: "Invalid ISO datetime" });
